import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Injector, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatRadioModule } from '@angular/material/radio';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { ConfirmationDialogComponent, LoadingOverlayComponent, SnackbarService } from '@shared/ui';
import { PipelineEnvironment } from '@telespot/domain';
import type { PipelineStepDraftDto } from '@protocols/use-cases';
import { Observable, of, Subject } from 'rxjs';
import { filter, map, switchMap, take, takeUntil, withLatestFrom } from 'rxjs/operators';

import { PipelineStepListItem } from '../../../state/pipeline-editor.state';
import { EDITOR_COMMIT_MODE } from '../../editor-commit-mode.token';
import { EDITOR_DIALOG_LARGE } from '../../editor-dialog-sizes';
import { PipelineStepManagementListComponent } from '../pipeline-step-management-list/pipeline-step-management-list.component';
import { PipelineStepEditorDialogComponent } from '../pipeline-step-editor/pipeline-step-editor-dialog.component';

import { PipelineEditorFacade } from './pipeline-editor.facade';

type LangControls = { [lang: string]: FormControl<string> };

interface PipelineEditorForm {
  name: FormGroup<LangControls>;
  description: FormGroup<LangControls>;
  env: FormControl<PipelineEnvironment>;
}

@Component({
  selector: 'ts-pipeline-editor',
  templateUrl: './pipeline-editor.component.html',
  styleUrls: ['./pipeline-editor.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    MatRadioModule,
    MatTooltipModule,
    MatDialogModule,
    LoadingOverlayComponent,
    PipelineStepManagementListComponent,
  ],
})
export class PipelineEditorComponent implements OnInit, OnDestroy {
  public readonly id$ = this.facade.id$;
  public readonly loading$ = this.facade.loading$;
  public readonly saving$ = this.facade.saving$;
  public readonly error$ = this.facade.error$;
  public readonly canChangeEnv$ = this.facade.canChangeEnv$;
  public readonly hasSteps$ = this.facade.hasSteps$;
  public readonly displayNameValid$ = this.facade.displayNameValid$;
  public readonly stepListItems$ = this.facade.stepListItems$;
  public readonly stepListLoading$ = this.facade.busy$;
  public readonly stepListDisabled$ = this.facade.stepListDisabled$;
  public readonly isAdmin$ = this.facade.isAdmin$;
  public readonly supportedLanguages = this.facade.supportedLanguages;

  public readonly form: FormGroup<PipelineEditorForm>;
  public readonly PipelineEnvironment = PipelineEnvironment;

  /**
   * Confirm-button label follows the host's commit semantics: staging hosts
   * (dialog wrappers) read "Continue", persisting hosts (pages) read "Save".
   */
  public readonly confirmLabelKey =
    inject(EDITOR_COMMIT_MODE) === 'stage' ? 'button.continue' : 'button.save';

  private readonly destroy$ = new Subject<void>();

  constructor(
    public readonly facade: PipelineEditorFacade,
    private readonly dialog: MatDialog,
    private readonly injector: Injector,
    private readonly snackbar: SnackbarService
  ) {
    const nameControls: LangControls = {};
    const descControls: LangControls = {};
    for (const lang of this.facade.supportedLanguages) {
      nameControls[lang.value] = new FormControl('', {
        nonNullable: true,
        validators: lang.value === 'en' ? [Validators.required] : [],
      });
      descControls[lang.value] = new FormControl('', { nonNullable: true });
    }
    this.form = new FormGroup<PipelineEditorForm>({
      name: new FormGroup<LangControls>(nameControls),
      description: new FormGroup<LangControls>(descControls),
      env: new FormControl<PipelineEnvironment>(PipelineEnvironment.CLOUD, {
        nonNullable: true,
      }),
    });
  }

  ngOnInit(): void {
    this.facade.loadRoles();
    // state → form (seeded once the load resolves; in create mode loading
    // never flips to true so this fires immediately with the empty defaults)
    this.facade.loading$
      .pipe(
        filter((loading) => !loading),
        take(1),
        withLatestFrom(this.facade.name$, this.facade.description$, this.facade.env$),
        takeUntil(this.destroy$)
      )
      .subscribe(([, name, description, env]) => {
        this.form.controls.name.patchValue(name, { emitEvent: false });
        this.form.controls.description.patchValue(description, { emitEvent: false });
        this.form.controls.env.setValue(env, { emitEvent: false });
      });

    // form → state
    this.form.controls.name.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => this.facade.setName(value));
    this.form.controls.description.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => this.facade.setDescription(value));
    this.form.controls.env.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((env) => this.facade.setEnv(env));

    // Gate the form on the facade's per-control editability signal; non-admins
    // get a read-only editor and the env control follows canChangeEnv$.
    this.facade.formEditability$.pipe(takeUntil(this.destroy$)).subscribe(({ name, description, env }) => {
      this.setEnabled(this.form.controls.name, name);
      this.setEnabled(this.form.controls.description, description);
      this.setEnabled(this.form.controls.env, env);
    });

    // Open the step dialog when the openStepDialog$ signal is emitted
    this.facade.openStepDialog$.pipe(takeUntil(this.destroy$)).subscribe(() => this.openStepDialog());

    // Cascade snackbar dispatchers. Long duration (caller-controlled dismiss)
    // because the message references a specific label and the user needs time
    // to read it before deciding to retry.
    this.facade.labelSaveFailed$
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ name }) =>
        this.snackbar.show('pipeline_editor.snackbar.label_save_failed', { duration: 0, params: { name } })
      );
    this.facade.labelConfigSaveFailed$
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ name }) =>
        this.snackbar.show('pipeline_editor.snackbar.label_config_save_failed', { duration: 0, params: { name } })
      );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onAddStep(): void {
    this.facade.beginAddStep();
  }

  public onEditStep(item: PipelineStepListItem): void {
    this.facade.beginEditStep(parseInt(item.uuid, 10));
  }

  public onRemoveStep(item: PipelineStepListItem): void {
    this.facade.removeStep(parseInt(item.uuid, 10));
  }

  public onStepsReordered(items: PipelineStepListItem[]): void {
    this.facade.reorderSteps(items);
  }

  public onSave(): void {
    this.facade.save();
  }

  public onCancel(): void {
    this.facade.hasChanges$.pipe(take(1), takeUntil(this.destroy$)).subscribe((hasChanges) => {
      if (!hasChanges) {
        this.facade.cancel();
        return;
      }
      this.confirmDiscard$()
        .pipe(takeUntil(this.destroy$))
        .subscribe((discard) => {
          if (discard) this.facade.cancel();
        });
    });
  }

  /**
   * Exit gate for navigation-driven dismissal (browser back while the hosting
   * dialog is open): clean sessions exit silently; dirty sessions ask for
   * confirmation and, when the user discards, run the real cancel flow so the
   * dialog closes through the shared `outcome$` handoff.
   */
  public canDeactivate(): Observable<boolean> | boolean {
    return this.facade.hasChanges$.pipe(
      take(1),
      switchMap((hasChanges) => {
        if (!hasChanges) return of(true);
        return this.confirmDiscard$().pipe(
          map((discard) => {
            if (discard) this.facade.cancel();
            return discard;
          })
        );
      })
    );
  }

  private confirmDiscard$(): Observable<boolean> {
    return this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: 'pipeline_editor.warnings.cancel_unsaved.title',
          text: 'pipeline_editor.warnings.cancel_unsaved.text',
          acceptButtonText: 'button.discard_changes',
          cancelButtonText: 'pipeline_step_editor.button.keep_editing',
          warningDialog: true,
          defaultAction: 'cancel',
        },
        hasBackdrop: true,
      })
      .afterClosed()
      .pipe(
        take(1),
        map((result) => result === 'accept')
      );
  }

  private setEnabled(control: AbstractControl, enabled: boolean): void {
    if (enabled) control.enable({ emitEvent: false });
    else control.disable({ emitEvent: false });
  }

  private openStepDialog(): void {
    this.dialog
      .open<PipelineStepEditorDialogComponent, unknown, PipelineStepDraftDto | undefined>(
        PipelineStepEditorDialogComponent,
        {
          ...EDITOR_DIALOG_LARGE,
          hasBackdrop: true,
          injector: this.injector,
        }
      )
      .afterClosed()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((draft) => this.facade.completeStepEdit(draft ?? null));
  }
}
